import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import PageHeader from "common/PageHeader";
import Footer from "common/Footer";
import MDBox from "components/MDBox";
import LLMSettingsForm from "common/Settings/LLMSettings";
import UserManagement from "layouts/users/UserManagement";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import api from "services/api";
import GeneralTab from "./GeneralTab";
import CompanyDetailsTab from "./CompanyDetailsTab";
import DocumentReceiptTab from "./DocumentReceiptTab";
import ApiKeysTab from "./ApiKeysTab";
import ModelsTab from "./ModelsTab";

// key, label, applies to the selected company only, administrators only
const TABS = [
  { key: "general", label: "stTabGeneral", company: false, admin: false },
  { key: "company", label: "stTabCompany", company: true, admin: false },
  { key: "receipt", label: "stTabReceipt", company: true, admin: false },
  { key: "ai", label: "stTabRecognition", company: true, admin: false },
  { key: "models", label: "stTabModels", company: false, admin: true },
  { key: "api-keys", label: "stTabApiKeys", company: false, admin: true },
  { key: "users", label: "stTabUsers", company: false, admin: true },
];

function SettingsPage() {
  const { t } = useLanguage();
  const { selectedCompany, setSelectedCompany, companies } = useCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api
      .get("/api/admin/me/")
      .then(({ data }) => setIsAdmin(!!data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const visibleTabs = useMemo(() => TABS.filter((tab) => !tab.admin || isAdmin), [isAdmin]);
  const requested = searchParams.get("tab");
  const current = visibleTabs.find((tab) => tab.key === requested) || visibleTabs[0];
  const companyId = selectedCompany?.id;

  const openTab = (key) => setSearchParams({ tab: key }, { replace: true });

  const renderTab = () => {
    if (current.company && !companyId) {
      return (
        <Alert severity="info" sx={{ mt: 3 }}>
          {t("stSelectCompany")}
        </Alert>
      );
    }
    // key={companyId}: switching the company reloads the tab with that company's settings
    switch (current.key) {
      case "company":
        return <CompanyDetailsTab key={companyId} companyId={companyId} />;
      case "receipt":
        return <DocumentReceiptTab key={companyId} companyId={companyId} />;
      case "ai":
        return <LLMSettingsForm key={companyId} companyId={String(companyId)} />;
      case "models":
        return <ModelsTab />;
      case "api-keys":
        return <ApiKeysTab />;
      case "users":
        return <UserManagement embedded />;
      default:
        return <GeneralTab />;
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Card
          sx={{
            overflow: "visible",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
          }}
        >
          <PageHeader
            title={t("stTitle")}
            subtitle={t("stSubtitle")}
            icon={<TuneIcon color="info" />}
          />
          <Box sx={{ px: { xs: 1, md: 2 }, borderBottom: "1px solid", borderColor: "divider" }}>
            <Tabs
              value={current.key}
              onChange={(event, key) => openTab(key)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 48,
                // The theme stretches tabs to equal widths; on phones they would be squeezed – let them scroll instead.
                "& .MuiTab-root": {
                  minHeight: 48,
                  flex: { xs: "0 0 auto", md: "1 1 auto" },
                  whiteSpace: "nowrap",
                  px: 2,
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <Tab key={tab.key} value={tab.key} label={t(tab.label)} />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              px: { xs: 2, md: 3 },
              // The theme sets Select padding to 0 (squashed fields); give selects the height of text fields.
              "& .MuiOutlinedInput-root .MuiSelect-select": {
                padding: "12px 14px !important",
                minHeight: "1.4375em",
              },
              "& .MuiInputBase-sizeSmall .MuiSelect-select": { padding: "8.5px 14px !important" },
            }}
          >
            {current.company && (
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
                sx={{ pt: 3, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}
              >
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel>{t("stCompanyScope")}</InputLabel>
                  <Select
                    value={companyId || ""}
                    label={t("stCompanyScope")}
                    onChange={(event) => {
                      const company = companies.find((c) => c.id === event.target.value);
                      if (company) setSelectedCompany(company);
                    }}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  {t("stCompanyScopeHint")}
                </Typography>
              </Box>
            )}
            {(current.key === "api-keys" || current.key === "models") && (
              <Box pt={3}>
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={t("stAllCompaniesScope")}
                />
              </Box>
            )}
            {renderTab()}
          </Box>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SettingsPage;
